import { useEffect } from "react";
import { useLocation, Link, useParams } from "wouter";
import { useAuth, logout } from "@/hooks/use-auth";
import { useListEngineerJobs, getListEngineerJobsQueryKey } from "@workspace/api-client-react";
import { Wrench, Loader2, LogOut, ChevronRight, ArrowLeft, Check, Edit2, AlertTriangle, Map } from "lucide-react";
import logo from "/images/brand/vertex-logo.png";

export default function EngineerJobs({ kind }: { kind: "callout" | "site_survey" }) {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  
  const { data: jobs, isLoading: isJobsLoading, isError } = useListEngineerJobs({
    query: {
      enabled: !!user && user.role === "engineer",
      queryKey: getListEngineerJobsQueryKey(),
      refetchInterval: 15000,
      staleTime: 0
    }
  });

  useEffect(() => {
    if (!isAuthLoading) {
      if (!user) {
        setLocation("/admin"); 
      } else if (user.role === "admin") {
        setLocation("/admin/dashboard");
      }
    }
  }, [user, isAuthLoading, setLocation]);

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const filteredJobs = jobs?.filter(j => j.kind === kind) || [];
  
  const title = kind === "callout" ? "Call Outs" : "Site Surveys";
  const Icon = kind === "callout" ? AlertTriangle : Map;
  const iconColor = kind === "callout" ? "text-rose-600" : "text-blue-600";
  const iconBg = kind === "callout" ? "bg-rose-50" : "bg-blue-50";

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Jobs</h2>
        <p className="text-slate-600 mb-6">There was a problem loading your assigned work.</p>
        <Link href="/engineer" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">Return to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col">
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="VertexAir" className="h-7 brightness-0 invert" />
            <div className="h-6 w-px bg-slate-700 mx-1"></div>
            <div className="flex items-center gap-2 text-white">
              <Wrench className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Engineer Portal</span>
            </div>
          </div>
          <button onClick={logout} className="p-2 -mr-2 text-slate-400 hover:text-white transition-colors" title="Logout">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto p-4 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <Link href="/engineer" className="p-2 -ml-2 rounded-lg hover:bg-slate-200 text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        </div>
        
        <p className="text-sm text-slate-500">Manage your assigned {title.toLowerCase()}</p>

        {isJobsLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
        ) : (
          <div className="space-y-3">
            {filteredJobs.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${iconBg} ${iconColor}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-slate-900">No {title.toLowerCase()} assigned</h3>
                <p className="text-sm text-slate-500 mt-1">You're all caught up for now.</p>
              </div>
            ) : (
              filteredJobs.map(job => (
                <div key={job.id} onClick={() => setLocation(`/engineer/jobs/${job.id}`)} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-primary/40 hover:shadow-sm transition-all active:scale-[0.99]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {job.status === "submitted" ? (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Check className="w-3 h-3" /> Submitted
                        </span>
                      ) : job.status === "cancelled" ? (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-slate-50 text-slate-700 border border-slate-200">
                           Cancelled
                        </span>
                      ) : (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          <Edit2 className="w-3 h-3" /> {job.status === "assigned" ? "Assigned" : "In Progress"}
                        </span>
                      )}
                      
                      {job.priority === "urgent" && (
                        <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-red-50 text-red-700 border border-red-200">
                          Urgent
                        </span>
                      )}
                      <span className="text-xs text-slate-400 shrink-0 font-medium">{new Date(job.scheduledDate).toLocaleDateString()}</span>
                    </div>
                    <h3 className="font-bold text-slate-900 truncate">{job.site.name || "Unnamed Site"}</h3>
                    <p className="text-sm text-slate-500 truncate">{job.title}</p>
                  </div>
                  <div className="shrink-0 w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
